import { useEffect, useRef } from "react";

function isBrowserRuntime(): boolean {
  return globalThis.window !== undefined && globalThis.document !== undefined;
}

function isElementFullyVisible(el: HTMLElement): boolean {
  const rect = el.getBoundingClientRect();
  const viewportHeight = globalThis.window.innerHeight || document.documentElement?.clientHeight || 0;
  const viewportWidth = globalThis.window.innerWidth || document.documentElement?.clientWidth || 0;

  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <= viewportHeight &&
    rect.right <= viewportWidth
  );
}

function focusElement(el: HTMLElement): void {
  if (!el.hasAttribute("tabindex")) {
    el.setAttribute("tabindex", "-1");
  }

  if (typeof el.focus === "function") {
    try {
      el.focus({ preventScroll: true });
    } catch {
      el.focus();
    }
  }
}

/**
 * Scrolls the referenced element into view (centered, smooth) if it is not fully visible in the viewport.
 */
export function useScrollIntoViewIfNotVisible(
  ref: React.RefObject<HTMLElement | null>,
  trigger: boolean,
) {
  const prevTriggerRef = useRef(false);

  useEffect(() => {
    const el = ref.current;

    if (!isBrowserRuntime() || !el) {
      prevTriggerRef.current = trigger;
      return;
    }

    if (trigger && !prevTriggerRef.current) {
      if (!isElementFullyVisible(el)) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }

      focusElement(el);
    }

    prevTriggerRef.current = trigger;
  }, [trigger, ref]);
}
